import { createFileRoute, notFound } from "@tanstack/react-router";
import { getPublicProduct } from "api-client";
import { z } from "zod";

import { PublicProductNotFound } from "src/components/PublicProductNotFound";
import { loaderHandler } from "src/lib/loaderHandler";
import { PublicProductPage } from "src/pages/PublicProductPage";
import { getPublicProductUrl } from "src/profile/publicProductUrl";

const routeSearchSchema = z.object({
  price: z.string().optional(),
});

export const Route = createFileRoute("/c/$slug_/p/$productSlug")({
  component: RoutePage,
  notFoundComponent: PublicProductNotFound,
  ssr: true,
  shouldReload: false,
  validateSearch: routeSearchSchema,
  async loader(args) {
    return await loaderHandler(async () => {
      const response = await getPublicProduct({
        path: {
          coachSlug: args.params.slug,
          productSlug: args.params.productSlug,
        },
        throwOnError: false,
      });

      if (response.response?.status === 404) {
        throw notFound();
      }

      if (response.error) {
        throw response.error;
      }

      return {
        data: { product: response.data.data },
      };
    });
  },
  head(args) {
    if (!args.loaderData?.data) {
      return { meta: [] };
    }

    const product = args.loaderData.data.product;
    const title = `${product.name} | ${product.coach.displayName}`;
    const fullDescription =
      product.description.replace(/\s+/g, " ").trim() ||
      `${product.name} by ${product.coach.displayName} on sztama.`;
    const description =
      fullDescription.length > 200
        ? `${fullDescription.slice(0, 199).trimEnd()}…`
        : fullDescription;
    const canonicalUrl = getPublicProductUrl(product.coach.slug, product.slug);
    const imageUrl =
      product.imageUrl ??
      product.coach.avatarUrl ??
      "https://sztama.app/og-image.png";
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Product",
      url: canonicalUrl,
      name: product.name,
      description: fullDescription,
      image: imageUrl,
      brand: { "@type": "Person", name: product.coach.displayName },
      offers: product.prices.map((price) => ({
        "@type": "Offer",
        url: canonicalUrl,
        price: (price.unitAmount / 100).toFixed(2),
        priceCurrency: price.currency.toUpperCase(),
        availability: product.canBuy
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      })),
    };

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:site_name", content: "sztama" },
        { property: "og:image", content: imageUrl },
        { property: "og:image:alt", content: product.name },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: imageUrl },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(structuredData).replace(/</g, "\\u003c"),
        },
      ],
    };
  },
  staticData: {
    getLayoutSettings() {
      return {
        showAuthSidebar: false,
        useAuthLayout: false,
      };
    },
  },
});

function RoutePage() {
  const loaderData = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <PublicProductPage
      product={loaderData.data.product}
      selectedPriceId={search.price}
      onPriceChange={(price) => {
        navigate({
          replace: true,
          resetScroll: false,
          search: (previous) => ({ ...previous, price }),
        });
      }}
    />
  );
}
